import { PlusCircle, SealCheck } from "@phosphor-icons/react"
import CustomCard from "components/Atoms/CustomCard"
import CustomTag from "components/Atoms/CustomTag"
import ReactTableHeader from "components/Common/ReactTableHeader"
import TablePagination from "components/Molecules/TablePagination"
import { useInwardAllocationsQuery } from "generated/graphql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import React from "react"
import ReactTable from "react-table"
import StackCardDialog from "./StackCard"
import { NavLink } from "react-router-dom"
import dayjs from "dayjs"
import { useFilter } from "store/search-filter/search-filter.reducer"
import SearchFilter, {
  SearchFilterCallout,
} from "components/Organisms/SearchFilter"
import { VARIETIES } from "graphql/selector.queries.gql"
import DynamicSelector from "components/Molecules/DynamicSelector"
const Allocation = () => {
  const [{ page, search }, setFilter] = useSearchFilter({
    group: "*",
  })

  const [filter] = useFilter("depot")
  const [filterAllocation, setFilterAllocation] = useFilter("allocation")
  const COLUMNS = [
    {
      Header: "Date",
      accessor: "createdAt",
      Cell({ value, index }) {
        return (
          <div>
            <p>
              {dayjs(value).format("DD MMM 'YY")}
              <br />
              {dayjs(value).format("hh:mm A")}
            </p>
          </div>
        )
      },
    },
    {
      Header: "Gate Passes",
      accessor: "inward.booking.schedule.gatePass",
      Cell: ({ value }) => {
        return (
          <CustomTag intent="primary" round minimal>
            {value}
          </CustomTag>
        )
      },
    },
    {
      Header: "Suppliers",
      accessor: "inward.booking.schedule.plot.farmer.fullName",
      Cell({ value }) {
        return (
          <CustomTag intent="none" className="bg-slate-200" round minimal>
            {value}
          </CustomTag>
        )
      },
    },
    {
      Header: "Aggregator",
      accessor: "inward.booking.schedule.aggregator.fullName",
      Cell({ value }) {
        return <p className="text-center">{value}</p>
      },
    },

    {
      Header: "Vehicle Number",
      accessor: "inward.booking.schedule.vehicle.registrationNumber",

      Cell({ value }) {
        return <p className="text-center">{value}</p>
      },
    },
    {
      Header: "Variety",
      accessor: "inward.booking.schedule.variety.name",

      Cell({ value }) {
        return <div className="flex items-center justify-center">{value}</div>
      },
    },
    {
      Header: "Inwarded Weight",
      accessor: "inward.weigh.netWeight",

      Cell({ value }) {
        return (
          <p className="text-center">
            {value} <span className="text-gray-500 text-sm">Kg</span>
          </p>
        )
      },
    },
    {
      Header: "Inwarded SKU's ",
      accessor: "inward.weigh.inwardedQuantity",

      Cell({ value, original }) {
        return (
          <p className="text-center">
            {value}{" "}
            <span className="text-gray-500 text-sm">
              {original?.inward?.weigh?.skuType?.name}
            </span>
          </p>
        )
      },
    },
    {
      Header: "Inwarded Status",
      accessor: "inward.id",

      Cell({ value }) {
        return (
          <div className="flex items-center justify-center">
            <SealCheck size={24} weight="fill" color="#1C6E42" />
          </div>
        )
      },
    },
    {
      Header: "Stack",
      accessor: "holdingArea",

      Cell({ value, original }) {
        return (
          <div className="flex items-center justify-center">
            <StackCardDialog inwardAllocations={original} />
          </div>
        )
      },
    },
    {
      Header: "Holding Area",
      accessor: "id",

      Cell({ value, original }) {
        const isHoldingAreaAdded: boolean = original?.holdingArea != null
        const isGreaterThanZero: boolean =
          (original?.availableSkusCount ?? 0) > 0
        const confirm = JSON.parse(original?.metadata ?? "{}")?.confirm

        return (
          <div className="flex items-center justify-center gap-2">
            {isHoldingAreaAdded ? (
              isGreaterThanZero ? (
                <div className="flex flex-col items-center gap-1">
                  <NavLink to={`/d/allocation/${value}`}>
                    <span className="flex items-center gap-1 cursor-pointer">
                      <PlusCircle color="#1C6E42" size={20} />
                      Assign
                    </span>
                  </NavLink>
                  <span className="text-sm text-gray-700">
                    {original?.holdingArea?.name}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <NavLink to={`/d/allocation/${value}`}>
                    <SealCheck
                      size={24}
                      weight={confirm ? "fill" : "regular"}
                      color={confirm ? "#1C6E42" : "#3175D3"}
                    />
                  </NavLink>
                  <span className="text-sm text-gray-700">
                    {original?.holdingArea?.name}
                  </span>
                </div>
              )
            ) : (
              <span className="flex items-center gap-1 cursor-default text-gray-500">
                <PlusCircle color="#1C6E42" size={20} />
                Assign
              </span>
            )}
          </div>
        )
      },
    },
  ]

  const {
    data: iData,
    loading,
    error,
  } = useInwardAllocationsQuery({
    variables: {
      search: search ?? "",
      limit: 10,
      offset: page,
      depotId: filter?.depot?.id,
      varietyId: filterAllocation?.variety?.id,
    },
    fetchPolicy: "network-only",
  })
  const list = iData?.inwardAllocations ?? []
  if (error) return <>{`Errors ${error.message}`}</>

  return (
    <CustomCard className="p-0">
      <ReactTableHeader
        icon="diagram-tree"
        title="Allocation"
        value={search ?? ""}
        searchPlaceHolder="Search by Gate Pass, Supplier, Aggregator"
        filter={
          <SearchFilter
            value={filterAllocation}
            className="ml-3"
            getValues={setFilterAllocation}
            onClear={() => setFilterAllocation({})}
          >
            <SearchFilterCallout title="Variety" fill>
              <DynamicSelector
                fill
                big
                formClassName="col-span-2"
                query={VARIETIES}
                variables={{
                  includeCommodity: false,
                }}
                path="variety"
                value={filterAllocation?.variety}
                onChange={(_path, value) => {
                  setFilterAllocation(prev => ({
                    ...prev,
                    variety: value,
                  }))
                }}
              />
            </SearchFilterCallout>
          </SearchFilter>
        }
        onQueryChange={search => {
          setFilter(prev => ({
            ...prev,
            search,
            page: 0,
          }))
        }}
      >
        <TablePagination
          disabledNext={iData?.inwardAllocations.length < 10}
          className="mx-3"
          page={page}
          onPageChange={page => {
            setFilter(prev => ({
              ...prev,
              page,
            }))
          }}
        />

        {/* <ProductionActions /> */}
      </ReactTableHeader>

      <ReactTable
        className="mt-0 vt-master-table vt_user_table"
        loading={loading}
        pageSize={10}
        showPagination={false}
        resizable={true}
        data={list}
        columns={COLUMNS}
      />
    </CustomCard>
  )
}
export default Allocation
